import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Search, RefreshCw, Headphones, MessageSquare, X, Send,
  Clock, CheckCircle, AlertCircle, XCircle, User, Filter
} from 'lucide-react';

const STATUS_CONFIG = {
  open: { label: 'Aberto', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  pending: { label: 'Aguardando', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  resolved: { label: 'Resolvido', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  closed: { label: 'Fechado', color: 'bg-gray-100 text-gray-700', icon: XCircle }
};

const PRIORITY_CONFIG = {
  high: { label: 'Alta', color: 'bg-red-100 text-red-700' },
  medium: { label: 'Média', color: 'bg-yellow-100 text-yellow-700' },
  low: { label: 'Baixa', color: 'bg-blue-100 text-blue-700' }
};

export default function AdminSupport() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: '' });
  
  // Modal de detalhes/resposta
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchTickets();
  }, [user, navigate]);

  const fetchTickets = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      
      const res = await axios.get(`${API}/admin/dashboard-v2/tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(res.data.tickets || []);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error(error);
      // Mostrar estado vazio se o endpoint não existir
      setTickets([]);
      setStats({ open: 0, pending: 0, resolved: 0, closed: 0 });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const openTicketDetail = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/dashboard-v2/tickets/${ticket.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketDetail(res.data.ticket);
    } catch (error) {
      setTicketDetail(ticket);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/dashboard-v2/tickets/${selectedTicket.id}/reply`, {
        message: replyMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resposta enviada!');
      setReplyMessage('');
      openTicketDetail(selectedTicket);
    } catch (error) {
      toast.error('Erro ao enviar resposta');
    } finally {
      setSending(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!selectedTicket) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/dashboard-v2/tickets/${selectedTicket.id}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket fechado!');
      setSelectedTicket(null);
      fetchTickets(true);
    } catch (error) {
      toast.error('Erro ao fechar ticket');
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR', { 
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matches = 
        ticket.subject?.toLowerCase().includes(search) ||
        ticket.user?.name?.toLowerCase().includes(search) ||
        ticket.user?.email?.toLowerCase().includes(search);
      if (!matches) return false;
    }
    return true;
  });

  const clearFilters = () => setFilters({ search: '', status: '' });
  const hasFilters = filters.search || filters.status;

  if (loading) {
    return (
      <AdminLayout title="Suporte" subtitle="Carregando...">
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Suporte</h1>
            <p className="text-slate-500">Gerencie tickets de atendimento</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchTickets(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(STATUS_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <Card 
                key={key}
                className={`p-4 cursor-pointer transition-all ${filters.status === key ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setFilters({ ...filters, status: filters.status === key ? '' : key })}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats[key] || 0}</p>
                    <p className="text-xs text-slate-500">{config.label}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por assunto, cliente ou e-mail..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> Limpar
              </Button>
            )}
          </div>
        </Card>

        {/* Tickets List */}
        <div className="space-y-3">
          {filteredTickets.length > 0 ? (
            filteredTickets.map((ticket) => {
              const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
              const StatusIcon = statusConfig.icon;
              const priorityConfig = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium;
              
              return (
                <Card 
                  key={ticket.id}
                  className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => openTicketDetail(ticket)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${statusConfig.color}`}>
                      <StatusIcon className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold truncate">{ticket.subject || 'Ticket #' + ticket.id?.slice(0,8)}</p>
                        <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                        {ticket.priority && (
                          <Badge className={priorityConfig.color}>{priorityConfig.label}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <User className="w-3 h-3" />
                        <span>{ticket.user?.name || 'Usuário'}</span>
                        <span>•</span>
                        <span>{ticket.user?.email}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{formatDate(ticket.created_at)}</p>
                    </div>

                    <Button variant="ghost" size="sm">
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              );
            })
          ) : (
            <Card className="p-12 text-center">
              <Headphones className="w-16 h-16 mx-auto mb-4 text-slate-200" />
              <h3 className="text-lg font-semibold mb-2">Nenhum ticket encontrado</h3>
              <p className="text-slate-500">
                {hasFilters 
                  ? 'Tente ajustar os filtros'
                  : 'Os tickets de suporte aparecerão aqui'}
              </p>
            </Card>
          )}
        </div>
      </div>

      {/* Modal de Detalhes */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{ticketDetail?.subject || 'Ticket'}</DialogTitle>
          </DialogHeader>
          
          {ticketDetail && (
            <div className="space-y-4">
              {/* Info do ticket */}
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <Avatar>
                  <AvatarFallback>{getInitials(ticketDetail.user?.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium">{ticketDetail.user?.name || 'Usuário'}</p>
                  <p className="text-sm text-slate-500">{ticketDetail.user?.email}</p>
                </div>
                <Badge className={STATUS_CONFIG[ticketDetail.status]?.color}>
                  {STATUS_CONFIG[ticketDetail.status]?.label}
                </Badge>
              </div>

              {/* Mensagens */}
              <ScrollArea className="h-80 border rounded-lg p-4">
                <div className="space-y-4">
                  {/* PROMPT BLINDADO: Todas as mensagens agora vêm do array messages[] */}
                  {ticketDetail.messages?.map((msg, idx) => (
                    <div key={idx} className={`flex gap-3 ${msg.sender_type === 'admin' ? 'flex-row-reverse' : ''}`}>
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className={`text-xs ${msg.sender_type === 'admin' ? 'bg-primary text-white' : ''}`}>
                          {getInitials(msg.sender_name || ticketDetail.user?.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className={`flex-1 rounded-lg p-3 ${msg.sender_type === 'admin' ? 'bg-primary/10' : 'bg-slate-100'}`}>
                        <p className="text-sm">{msg.content}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatDate(msg.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Campo de resposta */}
              {ticketDetail.status !== 'closed' && (
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Digite sua resposta..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={2}
                    className="flex-1"
                  />
                  <Button onClick={handleReply} disabled={sending || !replyMessage.trim()}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {ticketDetail?.status !== 'closed' && (
              <Button variant="outline" onClick={handleCloseTicket}>
                <XCircle className="w-4 h-4 mr-2" /> Fechar Ticket
              </Button>
            )}
            <Button variant="ghost" onClick={() => setSelectedTicket(null)}>Voltar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
